import React, { useEffect, useState } from 'react';
import { Button, Datepicker, Label, Spinner } from 'flowbite-react';
import { FaArrowRight } from 'react-icons/fa';
import * as styles from './personalInfo.styles';
import CustomSelectInput from '../../../components/CustomSelectInput/CustomSelectInput';
import CustomInput from '../../../components/CustomInputs/CustomInput';
import FileUploadCard from '../../../components/FileUploadCard/FileUploadCard';
import featuredIcon from '../../../assets/Featured icon (1).png';
import client from '../../../api/client';
import showToast from '../../../components/CustomToast/toastHelper';
import { useNavigate } from 'react-router-dom';

interface PersonalInfoInputs {
    bvn: string;
    gender: string;
    dateOfBirth?: string;
    firstName: string;
    lastName: string;
    idNumber: string;
    idType: string;
    identification: File | null;
    identificationUrl?: string;
    proofOfAddress: File | null;
    proofOfAddressUrl?: string;
    passportPhoto: File | null;
    passportPhotoUrl?: string;
    mobileNumber: string;
}

const PersonalInfo = ({ changeCard }: any) => {

    const [formData, setFormData] = useState<PersonalInfoInputs>({
        bvn: '',
        gender: '',
        dateOfBirth: '',
        firstName: '',
        lastName: '',
        idNumber: '',
        idType: '',
        identification: null,
        identificationUrl: '',
        proofOfAddress: null,
        proofOfAddressUrl: '',
        passportPhoto: null,
        passportPhotoUrl: '',
        mobileNumber: ''
    });

    const [personalInfo, setPersonalInfo] = useState<any>()
    const [currentDob, setCurrentDob] = useState<Date | null>(null);
    const [idTypes, setIdTypes] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate();

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        if (name === 'mobileNumber') {
            // Remove any non-digit characters except the + prefix
            const cleanedValue = value.replace(/[^\d+]/g, '');
            
            // If the value doesn't start with +234, add it
            if (!cleanedValue.startsWith('+234')) {
                setFormData({ ...formData, [name]: '+234' });
            } else {
                // If it starts with +234, limit to 10 digits after that
                const digitsAfterPrefix = cleanedValue.slice(4);
                if (digitsAfterPrefix.length <= 10) {
                    setFormData({ ...formData, [name]: cleanedValue });
                }
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleFileInputChange = (file: File | null, fileType: string) => {
        setFormData({
            ...formData,
            [fileType]: file
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchIdTypes();
            await getPersonalDetails();
        };

        fetchData();
    }, []);

    const fetchIdTypes = async () => {
        try {
            const response = await client.get('/subsidiary/dashboard/onboarding/identification/types');
            setIdTypes(response.data.data);
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                console.error('error getting data', err);
            }
        }
    };

    useEffect(() => {
        if (personalInfo) {
            const dob = personalInfo?.dateOfBirth ? new Date(personalInfo.dateOfBirth) : null;
            // Adjust the date to account for timezone
            if (dob) {
                dob.setDate(dob.getDate() - 1);
            }
            setCurrentDob(dob);
            setFormData({
                bvn: personalInfo?.bvn ?? '',
                gender: personalInfo?.gender ?? '',
                dateOfBirth: personalInfo?.dateOfBirth ?? '',
                firstName: personalInfo?.firstName ?? '',
                lastName: personalInfo?.lastName ?? '',
                idNumber: personalInfo?.idNumber ?? '',
                idType: personalInfo?.idType ?? '',
                identification: null,
                identificationUrl: personalInfo?.identification ?? '',
                proofOfAddress: null,
                proofOfAddressUrl: personalInfo?.proofOfAddress ?? '',
                passportPhoto: null,
                passportPhotoUrl: personalInfo?.passportPhoto ?? '',
                mobileNumber: personalInfo?.mobileNumber ?? ''
            });
        }
    }, [personalInfo]);

    const handleSubmit = async () => {
        let payload = new FormData();
        payload.append("Bvn", formData.bvn);
        payload.append("Gender", formData.gender ?? "");
        payload.append("DateOfBirth", formData.dateOfBirth ?? "");
        payload.append("FirstName", formData.firstName);
        payload.append("LastName", formData.lastName);
        payload.append("IdNumber", formData.idNumber);
        payload.append("IdType", formData.idType);
        payload.append("MobileNumber", formData.mobileNumber);
        payload.append("Identification", formData.identification ?? "");
        payload.append("ProofOfAddress", formData.proofOfAddress ?? "");
        payload.append("PassportPhoto", formData.passportPhoto ?? "");

        try {
            setLoading(true)
            const response = await client.post("/subsidiary/dashboard/onboarding/personal/information", payload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            showToast(response.data.message, "success");
            navigate('/business/onboarding/kyc/business-info');
            localStorage.setItem("activeCard", "business Info");
            window.location.reload()
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
                showToast(message, "error");
            }
        }
        finally {
            setLoading(false)
        }
    };

    const getPersonalDetails = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/onboarding/personal/information/view");
            console.log(response.data.data);

            setPersonalInfo(response.data.data);
            setCurrentDob(new Date(response.data.data.dateOfBirth));
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                console.error('error getting data', err);
                showToast(err, "error");
            }
        }

    };

    return (
        <div className={styles.container}>
            <div className={styles.sectionContainer}>
                <img src={featuredIcon} className={styles.sectionIcon} alt="Featured Icon" />
                <p className={styles.sectionTitle}>Personal Information</p>
                <p className={styles.sectionSubtitle}>Kindly provide us with some personal information to get your dashboard ready</p>
            </div>
            <div>
                <div className={styles.inputContainer}>
                    <CustomInput
                        label='BVN (To get your BVN, dial *565*0# on your registered mobile number)'
                        name='bvn'
                        value={formData.bvn}
                        onChange={handleInputChange}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <CustomSelectInput
                        name='gender'
                        className={styles.selectInputlabel}
                        label='Gender'
                        items={["Male", "Female"]}
                        value={formData.gender}
                        onChange={(value) => setFormData({ ...formData, gender: value })}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <div>
                        <Label htmlFor='dob' value='Date of Birth' />
                    </div>
                    <Datepicker
                        id='dob'
                        value={currentDob ? new Date(currentDob.getTime() - currentDob.getTimezoneOffset() * 60000).toISOString().split('T')[0] : ''}
                        placeholder='Select a date of birth'
                        maxDate={new Date()}
                        onSelectedDateChanged={(date) => { 
                            
                            setFormData({ ...formData, dateOfBirth: date.toISOString() }); 
                            setCurrentDob(date);
                        }}
                    />
                </div>
                <div>
                    <CustomInput
                        label='Mobile Number (Should be in the format +234...)'
                        name='mobileNumber'
                        placeholder='+234'
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        maxLength={14} 
                    />
                </div>
                <div className={styles.inputContainer}>
                    <CustomSelectInput
                        name='idType'
                        className={styles.selectInputlabel}
                        label='Identification Document'
                        items={idTypes}
                        valueKey='id'
                        displayKey='name'
                        value={formData.idType}
                        onChange={(value) => setFormData({ ...formData, idType: value })}
                    />
                </div>
                <div className={styles.gridContainer}>
                    <div>
                        <CustomInput
                            label='First Name'
                            name='firstName'
                            value={formData.firstName}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div>
                        <CustomInput
                            label='Last Name'
                            name='lastName'
                            value={formData.lastName}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className={styles.inputContainer}>
                    <CustomInput
                        label='Identification Number'
                        name='idNumber'
                        value={formData.idNumber}
                        onChange={handleInputChange}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <FileUploadCard
                        label="Identification"
                        onChange={(file) => handleFileInputChange(file, 'identification')}
                        subtitleText="Please upload any of the following means of identification: international passport, driver's license, national ID or voter's ID. All files must be an image (.jpg, .jpeg, .png or .pdf) with max size of 1mb."
                        current={formData.identification}
                    />
                    {formData.identificationUrl && !formData.identification && (
                        <div className={'flex justify-center items-center mx-auto h-28 w-28 rounded-full mt-2'}>
                            <img src={formData.identificationUrl} alt='Identification' className='w-full h-full object-cover' />
                        </div>
                    )}
                </div>
                <div className={styles.inputContainer}>
                    <FileUploadCard
                        label="Passport photograph"
                        onChange={(file) => handleFileInputChange(file, 'passportPhoto')}
                        current={formData.passportPhoto}
                    />
                    {formData.passportPhotoUrl && !formData.passportPhoto && (
                        <div className={'flex justify-center items-center mx-auto h-28 w-28 rounded-full mt-2'}>
                            <img src={formData.passportPhotoUrl} alt='Passport' className='w-full h-full object-cover' />
                        </div>
                    )}
                </div>
                <div className={styles.inputContainer}>
                    <FileUploadCard
                        label="Proof of address"
                        onChange={(file) => handleFileInputChange(file, 'proofOfAddress')}
                        current={formData.proofOfAddress}
                    />
                    {formData.proofOfAddressUrl && !formData.proofOfAddress && (
                        <div className={'flex justify-center items-center mx-auto h-28 w-28 rounded-full mt-2'}>
                            <img src={formData.proofOfAddressUrl} alt='Proof of Address' className='w-full h-full object-cover' />
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.saveButtonContainer}>
                <Button className={styles.saveButton} onClick={handleSubmit}>
                    {loading ? <Spinner size='sm' /> : <>
                        <p className={styles.saveButtonText}>Save details</p>
                        <FaArrowRight className={styles.arrowIcon} />
                    </>}
                </Button>
            </div>
        </div>
    );
};

export default PersonalInfo;
